import React, { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { fetchUsersMeta } from './api';
import BulkCreateUser from './BulkCreateUser';
import BulkEditUser from './BulkEditUser';
import IndividualCreateUser from './IndividualCreateUser';
import IndividualEditUser from './IndividualEditUser';
import { pillActive, pillInactive } from '../styles';
import { useOrderedSubtabs } from '../subtabs';
import { USERS_SUBTAB_CAPS, UsersMeta } from './types';

type SubtabKey = 'create-individual' | 'create-bulk' | 'edit-individual' | 'edit-bulk';

const SUBTABS: { key: SubtabKey; label: string; cap: string }[] = [
  {
    key: 'create-individual',
    label: 'Individual Create User',
    cap: USERS_SUBTAB_CAPS.createIndividual,
  },
  { key: 'create-bulk', label: 'Bulk Create User', cap: USERS_SUBTAB_CAPS.createBulk },
  {
    key: 'edit-individual',
    label: 'Individual Edit User',
    cap: USERS_SUBTAB_CAPS.editIndividual,
  },
  { key: 'edit-bulk', label: 'Bulk Edit User', cap: USERS_SUBTAB_CAPS.editBulk },
];

const AdminUsersTab: React.FC = () => {
  const declared = useMemo(() => SUBTABS, []);
  const visibleSubtabs = useOrderedSubtabs('users', declared);
  const [active, setActive] = useState<SubtabKey | null>(null);
  const [meta, setMeta] = useState<UsersMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setActive((current) => {
      if (current && visibleSubtabs.some((t) => t.key === current)) return current;
      return (visibleSubtabs[0]?.key as SubtabKey | undefined) ?? null;
    });
  }, [visibleSubtabs]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchUsersMeta();
        if (!cancelled) setMeta(data);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load user admin data');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  if (visibleSubtabs.length === 0) {
    return (
      <div className="text-sm text-[#8A8A8A]">
        You do not have access to any user-management tools in this panel.
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-5">
        {visibleSubtabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActive(tab.key)}
            className={`h-8 px-3 rounded-full text-[13px] font-medium transition-colors ${
              active === tab.key ? pillActive : pillInactive
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-sm text-[#8A8A8A]">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      )}
      {error && !loading && (
        <div className="rounded-md bg-red-50 text-red-700 px-3 py-2 text-sm">{error}</div>
      )}
      {!loading && !error && meta && active === 'create-individual' && (
        <IndividualCreateUser meta={meta} />
      )}
      {!loading && !error && meta && active === 'create-bulk' && (
        <BulkCreateUser meta={meta} />
      )}
      {!loading && !error && meta && active === 'edit-individual' && (
        <IndividualEditUser meta={meta} />
      )}
      {!loading && !error && meta && active === 'edit-bulk' && <BulkEditUser meta={meta} />}
    </div>
  );
};

export default AdminUsersTab;
